import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Alert, Button, Divider, Layout, message, Modal, Progress, Select, Typography} from 'antd';
import {AudioFilled, InfoCircleOutlined, VideoCameraFilled} from '@ant-design/icons';

import {useAuth} from "../../providers/AuthProvider";
import Video, {createLocalTracks} from "twilio-video";
import UserImageCircle from "../../components/UserImageCircle";
import {matchPath, Navigate, Route, Routes, useLocation, useNavigate} from "react-router-dom";
import InterviewSetup from "./video-interview/InterviewSetup";
import {useAPI} from "../../config/API";
import {useMutation} from "@tanstack/react-query";
import {useUnmount} from "react-use";
import InterviewComplete from "./video-interview/InterviewComplete";
import InterviewInfo from "./video-interview/InterviewInfo";
import ModalVideo from "react-modal-video";
import VideoFrame from "./video-interview/VideoFrame";


const {Header, Content, Sider} = Layout;
const {Title, Text, Paragraph} = Typography;
const {Option} = Select;
const TalentInterview = (props) => {
    const {auth, update: updateAuth, isUpdating} = useAuth();
    const location = useLocation();
    const [previewingVideo, setPreviewingVideo] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const [size, setSize] = useState(0);

    const [recordingTime, setRecordingTime] = useState(0);

    const [showPermissionsError, setShowPermissionsError] = useState(false)
    const [showExitPrompt, setShowExitPrompt] = useState(false);

    const [localTracks, setLocalTracks] = useState(null);
    const [twilioRoom, setTwilioRoom] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const localVideoRef = useRef();
    const remoteVideoRef = useRef();
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);

    const navigate = useNavigate();

    const [videoToken, setVideoToken] = useState(null)
    const removeLatestInterview = useMutation(
        ['deleteVideoMutation'],
        async (videoInterviewId) => {
            const removeVideoRequest = TB.request(
                TB.api_endpoint(`/api/talent/video-interviews/${videoInterviewId}/delete`),
            )

            let response = await removeVideoRequest.post().catch(console.warn);

            await TB.wait(1500);

            if (!response || !TB.has(response, 'success.data.steps')) {
                throw new Error('Something went wrong')
            }

            let updates = await auth.updateTalentVideoInterviewSteps(response.success.data.steps);

            if (updates && props.onChange) {
                await props.onChange(updates)
            }
            return startVideoPreview();
        }, {
            onError: (e) => {
                console.warn(e)
                message.error("Something went wrong. Please Try Again Later")
            }
        })
    const {
        data: steps,
        error,
        retry,
        isFetching,
    } = useAPI('fetchLatestVideoInterviewForTalent', {
        initialData: TB.map(TB.orderBy(laravel.talent_video_interview_steps || [], 'display_index', 'asc'), step => {
            if (TB.find(auth.talent_video_interviews, ['step.id', step.id])) {
                let {
                    step: stepInfo,
                    ...latest_interview
                } = TB.find(auth.talent_video_interviews, ['step.id', step.id]);
                return {
                    ...step,
                    latest_interview
                }
            }
            return step;
        }),
        onSuccess: (data) => {
            console.log('onSuccess', data)
        },
        onError: (error) => {
            console.log('onError', error)
        }
    })
    const currentStep = useMemo(() => {

        if (!steps?.length || isFetching) {
            return undefined
        }
        const match = matchPath({path: 'talent/application/video-interview/step/:step'}, location.pathname);
        if (!match?.params?.step) {
            return undefined
        }

        return TB.find(steps, ['display_index', parseInt(match.params.step)]);
    }, [location.pathname, isFetching, steps]);

    const nextStep = useMemo(() => {
        let next;
        if (currentStep) {
            next = TB.find(steps, ['display_index', currentStep.display_index + 1]);
        } else {
            let nextStepIndex = TB.find(steps, step => !step?.latest_interview)?.display_index;
            if (!TB.isDefined(nextStepIndex)) {
                nextStepIndex = TB.first(steps)?.display_index || 1
            }
            next = TB.find(steps, ['display_index', nextStepIndex]);
        }
        return next
    }, [currentStep, location.pathname, isFetching, steps]);

    const previousStep = useMemo(() => {
        let prev;
        if (currentStep) {
            prev = TB.find(steps, ['display_index', currentStep.display_index - 1]);
        }
        return prev
    }, [currentStep, location.pathname, isFetching, steps]);


    const startVideoPreview = async () => {
        try {
            const response = await window.axios.post('/api/talent/video-interviews/video-token');
            let token = response.data.success.data.token;
            await setVideoToken(token);

            await disconnectAllDevices();

            let tracks = await createLocalTracks({
                audio: {deviceId: selectedAudioDevice},
                video: {deviceId: selectedVideoDevice}
            })

            console.log('startVideoPreview', {audio: {deviceId: selectedAudioDevice}, video: {deviceId: selectedVideoDevice}})
            const audioTrack = tracks.find(track => track.kind === 'audio');
            const videoTrack = tracks.find(track => track.kind === 'video');

            await setLocalTracks({audio: audioTrack, video: videoTrack});


            const room = await Video.connect(token, {
                name: 'persistent-room-1' + token,
                tracks,
                audio: true,
                video: true,
            });

            await setTwilioRoom(room);

            // Attach the video track to the video element
            if (videoTrack && localVideoRef.current) {
                localVideoRef.current.innerHTML = '';
                let localVideo = videoTrack.attach(localVideoRef.current);
                localVideo.setAttribute("width", '100%');
                localVideo.setAttribute("height", 'auto');
                localVideo.style.objectFit = 'contain';
            }


            room.localParticipant.tracks.forEach(publishTrack);
            room.localParticipant.on('trackPublished', publishTrack);

            room.on('participantConnected', participant => {
                participant.tracks.forEach(publishTrack);
                participant.on('trackPublished', publishTrack);
            });

        } catch (error) {
            console.error('Error joining room:', error);
        }
    };

    const [selectedAudioDevice, setSelectedAudioDevice] = useState(TB.get(TB.storedPreferences(), `interview_preferences.audio`));
    const [selectedVideoDevice, setSelectedVideoDevice] = useState(TB.get(TB.storedPreferences(), `interview_preferences.video`));
    const [hasPermissions, setHasPermissions] = useState(null);

    const updateDevicesToLocalStorage = async (updates) => {
        let preferences = TB.get(TB.storedPreferences(), `interview_preferences`, {})
        return TB.persistStoredPreferences({
            'interview_preferences': {
                ...preferences,
                ...updates
            }
        })
    }
    const disconnectAllDevices = async () => {
        try {
            if (localTracks?.audio) {
                await localTracks?.audio.stop();
            }
            if (localTracks?.video) {
                await localTracks?.video.stop();
            }
            if (mediaRecorderRef?.current && mediaRecorderRef?.current?.state !== 'inactive') {
                await mediaRecorderRef?.current.stop();
            }
            if (twilioRoom) {
                await twilioRoom.remoteParticipants?.forEach((participant) => {
                    participant.tracks.forEach((publication) => {
                        if (publication.track) {
                            publication.track.detach();
                        }
                    });
                });
                await twilioRoom.disconnect();
            }
        } catch (e) {
            console.warn('ERROR: disconnectAllDevices', e)
        }
    }

    const enumerateDevices = async () => {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioDevices = devices.filter(device => device.kind === 'audioinput');
        const videoDevices = devices.filter(device => device.kind === 'videoinput');

        if (audioDevices.length && !selectedAudioDevice) {
            let selected = audioDevices[0].deviceId;
            let storedDevice = TB.get(TB.storedPreferences(), `interview_preferences.audio`);
            if (storedDevice && TB.find(audioDevices, ['deviceId', storedDevice])) {
                selected = storedDevice;
            }
            await setSelectedAudioDevice(selected)
        }
        if (videoDevices.length && !selectedVideoDevice) {
            let selected = videoDevices[0].deviceId;
            let storedDevice = TB.get(TB.storedPreferences(), `interview_preferences.video`);
            if (storedDevice && TB.find(videoDevices, ['deviceId', storedDevice])) {
                selected = storedDevice;
            }
            await setSelectedVideoDevice(selected)
        }
        ;
    };

    const requestPermissions = async () => {
        // Query existing permissions
        const permissions = await navigator.mediaDevices.enumerateDevices();

        const videoPermission = permissions.some(device =>
            device.kind === 'videoinput' && device.label !== ''
        );

        const audioPermission = permissions.some(device =>
            device.kind === 'audioinput' && device.label !== ''
        );

        console.log('requestPermissions', {videoPermission,audioPermission});
        if (videoPermission && audioPermission) {
            await setHasPermissions(true);
            return;
        }
        try {
            await navigator.mediaDevices.getUserMedia({video: true, audio: true});
            console.log('requestPermissions')

            await setHasPermissions(true);
        } catch (error) {
            message.error('Failed to get media permissions');
            await setHasPermissions(false);
            await setShowPermissionsError(true)
        }
    };

    const publishTrack = (publication) => {
        if (publication.isSubscribed) {
            attachTrack(publication.track);
        } else {
            publication.on('subscribed', attachTrack);
        }
    };

    const attachTrack = (track) => {
        if (track.kind === 'video') {
            track.attach(remoteVideoRef.current);
        } else if (track.kind === 'audio') {
            track.attach(remoteVideoRef.current);
        }
    };

    const getRecorderOptions = () => {
        let options = {mimeType: 'video/webm'};
        if (MediaRecorder.isTypeSupported('video/webm; codecs=vp9')) {
            options = {mimeType: 'video/webm; codecs=vp9'};
        } else if (MediaRecorder.isTypeSupported('video/webm')) {
            options = {mimeType: 'video/webm'};
        } else if (MediaRecorder.isTypeSupported('video/mp4')) {
            options = {mimeType: 'video/mp4', videoBitsPerSecond: 100000};
        } else {
            console.error("no suitable mimetype found for this device");
            TB.toast('Video not supported. Please contact support.', {theme: 'danger'});
            return false;
        }
        return options;
    }

    const startRecording = async () => {
        if (!twilioRoom) return;


        // console.log('startRecording', room.localParticipant.audioTracks)
        if (!localTracks?.audio || !localTracks?.video) {
            console.error('Audio or video track not found');
            return;
        }

        // Get the underlying MediaStreamTrack objects
        const audioStreamTrack = localTracks?.audio.mediaStreamTrack;
        const videoStreamTrack = localTracks?.video.mediaStreamTrack;

        // console.log('startRecording', audioStreamTrack, videoStreamTrack)
        const combinedStream = new MediaStream([audioStreamTrack, videoStreamTrack]);


        // console.log('startRecording', chunksRef.current)
        chunksRef.current = [];
        let recorderOptions = getRecorderOptions();
        if (!recorderOptions) {
            return;
        }
        mediaRecorderRef.current = new MediaRecorder(combinedStream, recorderOptions);

        mediaRecorderRef.current.ondataavailable = (event) => {
            if (event.data.size > 0) {
                chunksRef.current.push(event.data);
                // Calculate current size in MB
                const currentSize = chunksRef.current.reduce((acc, chunk) => acc + chunk.size, 0) / (1024 * 1024);
                setSize(currentSize);
            }
        };

        mediaRecorderRef.current.onstop = async () => {
            // console.log('onstop', isRecording)
            // stopRecording()
        };
        let currentRecordingTime = 3

        await setIsRecording(currentRecordingTime);

        await TB.wait(200)
        await setIsRecording(2.95);
        // await TB.wait(1200);
        while (parseInt(currentRecordingTime) >= 0 && currentRecordingTime !== true) {
            await TB.wait(600);
            currentRecordingTime = currentRecordingTime > 0 ? (currentRecordingTime - 1) : true;
            await setIsRecording(currentRecordingTime);
            await TB.wait(600);
            // console.log('startRecording', currentRecordingTime)
        }

        mediaRecorderRef.current.start(1000); // Collect data every second
    };

    const stopRecording = async () => {

        if (mediaRecorderRef.current && isRecording) {


            let recorderOptions = getRecorderOptions();
            if (!recorderOptions) {
                return;
            }
            await mediaRecorderRef.current.stop();
            await TB.wait(200);
            await setUploadProgress(true);
            await TB.wait(200);
            await setIsRecording(false);
            await setRecordingTime(0);

            try {
                const blob = new Blob(chunksRef.current, {type: recorderOptions.mimeType});
                const CHUNK_SIZE = 1024 * 1024; // 1MB chunks
                let uploadData = {
                    file_name: `video_${currentStep.display_index}.${recorderOptions.mimeType?.includes('webm') ? 'webm' : 'mp4'}`,
                    file_size: blob.size,
                    mime_type: recorderOptions.mimeType
                }

                // Initialize upload
                const uploadId = await initializeRecordingToServerMutation.mutateAsync(uploadData);

                // if (!uploadId) {
                //     throw new Error('Failed to initialize upload');
                // }
                const totalChunks = Math.ceil(blob.size / CHUNK_SIZE);

                // Upload chunks
                for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
                    const start = chunkIndex * CHUNK_SIZE;
                    const end = Math.min(start + CHUNK_SIZE, blob.size);
                    const chunk = blob.slice(start, end);

                    // Upload Chunk
                    const uploadResponse = await sendChunkRecordingToServerMutation.mutateAsync({
                        chunk: new File([chunk], 'chunk'),
                        upload_id: uploadId,
                        chunk_index: chunkIndex,
                        total_chunks: totalChunks,
                    });

                    console.log('stopRecording:uploadResponse', uploadResponse)
                    // if (!uploadResponse) {
                    //     throw new Error('Chunk upload failed');
                    // }
                    const totalProgress = (chunkIndex / totalChunks) * 100;

                    // console.log('uploadChunk: ' + chunkIndex, `Progress: ${totalProgress}`);
                    setUploadProgress(Math.round(totalProgress) || 1);

                    await TB.wait(400)
                }


                const updatedSteps = await completeRecordingToServerMutation.mutateAsync(uploadId);

                // if (!updatedSteps) {
                //     throw new Error('Failed to initialize upload');
                // }
                chunksRef.current = [];

                if (nextStep) {
                    await goToNextStep();
                }

                let updates = await auth.updateTalentVideoInterviewSteps(updatedSteps);
                if (updates && props.onChange) {
                    await props.onChange(updates);
                }

                setUploadProgress(0);
                TB.toast('Recording uploaded successfully!', {theme: 'success'});
            } catch (error) {
                console.error('Upload error:', error);
                await TB.showServerErrorToast();
                chunksRef.current = [];
                setUploadProgress(0);
            }
        }
    };

    const initializeRecordingToServerMutation = useMutation(['initializeRecordingToServer'], async (params) => {

        // Init upload
        const InitStreamRequest = TB.request(
            TB.api_endpoint('/api/talent/video-interviews/stream/initialize'), params);

        const initResponse = await InitStreamRequest.post().catch(console.warn);
        console.log('initResponse', initResponse)

        if (!initResponse?.success?.data?.upload_id) {
            throw new Error('Failed to initialize upload');
        }

        return initResponse.success.data.upload_id
    });

    const sendChunkRecordingToServerMutation = useMutation(['sendChunkRecordingToServer'], async (params) => {

        // Chunk upload
        const SendChunkRequest = TB.request(
            TB.api_endpoint('/api/talent/video-interviews/stream/upload-chunk'),
            params
        ).headers({
            'Content-Type': 'multipart/form-data'
        });

        const uploadResponse = await SendChunkRequest.post().catch(console.warn);
        // console.log('uploadChunkResponse' + chunkIndex, uploadResponse);

        if (!uploadResponse.success?.data) {
            throw new Error('Chunk upload failed');
        }

        return uploadResponse.success.data
    });
    const completeRecordingToServerMutation = useMutation(['completeRecordingToServer'], async (uploadId) => {

        // Complete upload
        const CompleteStreamRequest = TB.request(
            TB.api_endpoint(`/api/talent/video-interviews/${currentStep.id}/store/stream`), {
            upload_id: uploadId
        });

        const completeResponse = await CompleteStreamRequest.post().catch(console.warn);
        console.log('completeResponse', completeResponse)

        if (!completeResponse?.success?.data?.steps) {
            throw new Error('Failed to complete upload');
        }

        return completeResponse.success.data.steps
    });

    const isUploadingRecording = initializeRecordingToServerMutation.isLoading ||
        sendChunkRecordingToServerMutation.isLoading ||
        completeRecordingToServerMutation.isLoading || (uploadProgress === true || uploadProgress > 0);

    useEffect(() => {
        requestPermissions();
    }, []);

    useEffect(() => {
        if (selectedVideoDevice && twilioRoom && localTracks?.video) {
            startVideoPreview()
        }
    }, [selectedVideoDevice]);
    useEffect(() => {
        if (hasPermissions) {
            enumerateDevices();
        }
    }, [hasPermissions]);

    useEffect(() => {
        if (hasPermissions && !twilioRoom) {
            startVideoPreview();
        }
    }, [hasPermissions]);

    useUnmount(() => {
        if (twilioRoom) {
            twilioRoom.disconnect();
            console.log('twilioroomdisconnect', twilioRoom)
        }

        if (localTracks?.video) {
            localTracks.video.stop();
            console.log('localTracks.video.stop()', localTracks.video)
        }
        if (localTracks?.audio) {
            localTracks.audio.stop();
            console.log('localTracks.audio.stop', localTracks.audio)
        }
        if (mediaRecorderRef?.current && mediaRecorderRef?.current?.state !== 'inactive') {
            mediaRecorderRef.current.stop();
            console.log('mediaRecorderRef.stop', mediaRecorderRef.current)
        }
    })

    // Timer logic
    useEffect(() => {
        let interval;
        if (isRecording === true) {
            interval = setInterval(() => {
                setRecordingTime((prevTime) => prevTime + 1);
            }, 1000);
        }
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [isRecording]);


    useEffect(() => {
        if (currentStep && isRecording) {
            // console.log('currentStep', currentStep)
            mediaRecorderRef.current = null;
            chunksRef.current = [];
            setRecordingTime(0);
            setIsRecording(false);
        }
    }, [currentStep])




    const goToStep = async (step) => {
        if (step) {
            let shouldResetRoom = currentStep?.latest_interview && !step?.latest_interview
            await navigate(`/talent/application/video-interview/step/${step?.display_index}`);
            if (shouldResetRoom) {
                return startVideoPreview();
            }
        }
    }

    const goToNextStep = async () => {
        if (nextStep) {
            return goToStep(nextStep)
        } else {
            return props.onComplete(() => navigate(`/talent/application/video-interview`, {replace: true}));
        }
    }
    const goToPreviousStep = async () => {
        return goToStep(previousStep)
    }

    // if (currentStep && hasPermissions === false) {
    //     return <Navigate to={'/talent/application/video-interview/setup'} replace={true}/>
    // }
    if (location.pathname.includes('review')) {
        return (
            <InterviewComplete/>
        )
    }
    return (
        <Layout className="min-h-screen flex flex-row flex-wrap">
            <Content className="p-6 bg-gray-100 flex-grow">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between">
                        <Title level={4} className="m-0 font-medium">
                            Video Interview
                            <span className="pl-3 italic text-sm">
                                * Required For Application
                            </span>
                        </Title>
                        <Button type="primary" className="m-0 w-36"
                                disabled={isFetching || isUploadingRecording}
                                onClick={() => setShowExitPrompt(true)}>
                            Exit
                        </Button>
                    </div>
                    <Divider className="my-2.5"/>


                    {currentStep?.latest_interview ? (
                        <div className="relative bg-gray-50 rounded-lg min-h-96">

                            <div className="text-center justify-center py-7 flex items-center flex-col">
                                <Text strong className="text-base mb-4">
                                    Question {currentStep.display_index}: {currentStep.question}
                                </Text>

                                <p className="text-xs font-medium mb-2 underline whitespace-nowrap">
                                    Your Submission
                                </p>
                                <div className="flex flex-col gap-3 items-center">
                                    <div className="flex flex-col gap-3 items-center">
                                        <div
                                            className="relative inline-flex items-start self-start flex-col bg-gray-200 rounded-lg overflow-hidden">
                                            <div className="relative max-w-96">
                                                <video src={currentStep.latest_interview.video_path}
                                                       controls={false}
                                                       width="100%" height="100%"
                                                       className="pointer-events-none rounded object-contain"/>
                                            </div>


                                            <div
                                                className="absolute inset-0 bg-black bg-opacity-20 flex flex-col items-center justify-center">
                                                <i className="fas fa-play text-white opacity-70 fa-2x clickable"
                                                   onClick={() => {
                                                       setPreviewingVideo(true)
                                                   }}/>
                                            </div>
                                        </div>
                                    </div>
                                    <Button type="default" loading={removeLatestInterview.isLoading}
                                            onClick={() => removeLatestInterview.mutate(currentStep.latest_interview.id)}>
                                        Delete & Retake Video Submission
                                    </Button>
                                </div>
                            </div>

                            <ModalVideo
                                channel="custom"
                                url={currentStep.latest_interview.video_path}
                                isOpen={!!previewingVideo}
                                onClose={() => setPreviewingVideo(false)}
                            />
                        </div>

                    ) : (
                        <React.Fragment>
                            <div className="flex items-center mb-4">
                                <UserImageCircle user={auth} initialsOnly size={60} className="mr-2"/>
                                <div className="flex flex-col gap-0.5">
                                    <Text className="text-sm m-0">{auth.fullName()}</Text>
                                    <Text type="secondary text-xs">{auth.job_title}</Text>
                                </div>
                            </div>
                            <div className="relative bg-gray-200 rounded-lg overflow-hidden"
                                 style={{paddingTop: '56.25%'}}>
                                <div className="absolute inset-0">
                                    {!hasPermissions && (
                                        <div className="absolute inset-0">
                                            <img src="/images/interview-backdrop.jpg" alt="EX"
                                                 className="w-full h-full object-cover"/>
                                        </div>
                                    )}

                                    {hasPermissions && (
                                        <video ref={localVideoRef} className="w-full h-full object-cover" autoPlay
                                               playsInline muted/>
                                    )}


                                    {hasPermissions && twilioRoom && (
                                        <React.Fragment>
                                            {(isRecording || !TB.isNaN(parseInt(isRecording))) ? (
                                                !TB.isNaN(parseInt(isRecording)) ? (
                                                    <div
                                                        className="absolute bg-opacity-60 bg-black inset-0 flex flex-col gap-3.5 items-center justify-center">
                                                        <Progress strokeLinecap="square" type="circle"
                                                                  strokeColor={TB.config('color.secondary')}
                                                                  strokeWidth={11}
                                                                  percent={(((3 - isRecording) / 3) * 100)}
                                                                  format={percent => (
                                                                      <span className="text-lg text-white">
                                                                          {`0:0${TB.ceil(isRecording)}`}
                                                                      </span>
                                                                  )}/>
                                                    </div>
                                                ) : (
                                                    <VideoFrame
                                                        currentTime={recordingTime}
                                                        recorder={mediaRecorderRef?.current}
                                                        onStopRecording={stopRecording}
                                                        step={currentStep}/>
                                                )
                                            ) : (
                                                <React.Fragment>
                                                    {(uploadProgress === true || uploadProgress > 0) ? (
                                                        <div
                                                            className="absolute bg-opacity-60 bg-black inset-0 flex flex-col gap-3.5 items-center justify-center">
                                                            <Progress strokeLinecap="square" type="circle"
                                                                      strokeColor={TB.config('color.secondary')}
                                                                      strokeWidth={11}
                                                                      percent={uploadProgress === true ? 0 : uploadProgress}
                                                                      format={percent => (
                                                                          <span className="text-lg text-white">
                                                                              {`${percent}%`}
                                                                          </span>
                                                                      )}/>
                                                            <Text className="text-white">Uploading video...</Text>
                                                        </div>
                                                    ) : (
                                                        currentStep && (
                                                            <div
                                                                className="absolute bg-opacity-60 bg-black inset-0 flex flex-col gap-3.5 items-center justify-center">
                                                                <Text
                                                                    className="mx-auto text-white font-medium text-center text-lg">
                                                                    Question {currentStep.display_index}: {currentStep.question}
                                                                </Text>
                                                                <div
                                                                    dangerouslySetInnerHTML={{__html: currentStep.description ? currentStep.description.replace(/<br\s*[\/]?>/gi, ". ") : ''}}
                                                                    className="text-center px-14 mx-auto [&>*]:text-white [&>*]:text-sm [&>p]:text-white [&>p]:text-sm text-white text-sm *:text-white *:text-sm"/>

                                                                <div
                                                                    className="flex flex-row gap-4 self-center px-8 mt-3">
                                                                    <Button type="primary" size="large"
                                                                            className="text-base min-w-[250px] py-8"
                                                                            onClick={() => startRecording()}>
                                                                        Begin Recording
                                                                    </Button>
                                                                </div>
                                                                <div className="flex flex-col items-center gap-2">
                                                                    <Text
                                                                        className="mx-auto text-white font-medium text-center text-base">
                                                                        Target
                                                                        Length: {currentStep.target_length_seconds} seconds
                                                                    </Text>
                                                                    <Text
                                                                        className="mx-auto text-white font-semibold text-center text-xs">
                                                                        (Note: Recording will automatically end once
                                                                        target length is met)
                                                                    </Text>
                                                                </div>
                                                            </div>
                                                        )
                                                    )}
                                                </React.Fragment>)}
                                        </React.Fragment>
                                    )}
                                    {hasPermissions === null ? (
                                        <div
                                            className="absolute bg-opacity-60 bg-black inset-0 flex items-center justify-center">
                                            <Text className="text-white text-center">
                                                Connecting your video and audio..
                                            </Text>
                                        </div>
                                    ) : (!hasPermissions && (
                                        <div
                                            className="absolute bg-opacity-60 bg-black inset-0 flex items-center justify-center">
                                            <Text className="text-white text-center">
                                                Connection to audio and video failed. Check your browser and grant
                                                permission to
                                                continue
                                            </Text>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <Text type="secondary" className="mt-2 block">
                                Tip: "Ensure good lighting and a tidy background."
                            </Text>
                        </React.Fragment>

                    )}


                    {(nextStep || previousStep) && (
                        <div className="flex flex-row mt-5 justify-end gap-3 items-center">
                            {previousStep && (
                                <Button type="default"
                                        className={TB.mapClass("m-0 w-36 bg-black hover:bg-black text-white border-none", {
                                            'opacity-70': isFetching || isUploadingRecording
                                        })}
                                        disabled={isFetching || isUploadingRecording}
                                        onClick={() => goToPreviousStep()}>
                                    Go Back
                                </Button>
                            )}
                            {nextStep && currentStep && (
                                <Button type={currentStep?.latest_interview ? "primary" : 'default'}
                                        className="m-0 w-36"
                                        disabled={isFetching || isUploadingRecording}
                                        onClick={() => goToNextStep()}>
                                    {currentStep?.latest_interview ? 'Next Question' : 'Skip Question'}
                                </Button>
                            )}
                            {!nextStep && currentStep && props.onComplete && auth.hasCompletedVideoInterview() && (
                                <Button type="primary" className="m-0 w-36"
                                        disabled={isFetching || isUploadingRecording}
                                        onClick={() => goToNextStep()}>
                                    Finish
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </Content>

            <Sider
                className="max-w-[550px] flex-grow bg-white p-6 flex flex-col items-stretch border-t border-gray-200">

                {!isFetching && (
                    <Routes>
                        <Route index element={<Navigate to={'setup'} replace={true}/>}/>
                        <Route path="setup" element={<InterviewSetup
                            onStart={() => goToNextStep()}
                            showPermissionsError={() => setShowPermissionsError(true)}
                            hasPermissions={hasPermissions}
                            selectedVideoDevice={selectedVideoDevice}
                            setSelectedVideoDevice={async (device) => {
                                console.log('set selected', device);
                                await updateDevicesToLocalStorage({video: device});
                                setSelectedVideoDevice(device)
                            }}
                            selectedAudioDevice={selectedAudioDevice}
                            setSelectedAudioDevice={async (device) => {
                                console.log('set selected', device);
                                await updateDevicesToLocalStorage({audio: device});
                                setSelectedAudioDevice(device)
                            }}/>}/>

                        {steps?.length > 0 && <Route path={`step/:currentStep/*`} element={<InterviewInfo
                            isDisabled={isUploadingRecording || isRecording} steps={steps}
                            goToStep={goToStep}/>}/>}

                        {steps?.length > 0 && <Route path={'review'} element={<InterviewInfo
                            isDisabled={isUploadingRecording || isRecording} steps={steps}
                            goToStep={goToStep}/>}/>}
                        <Route path="*" element={<Navigate to={'setup'} replace={true}/>}/>
                    </Routes>
                )}
            </Sider>

            <ExitVideoPromptModal open={showExitPrompt} onClose={() => setShowExitPrompt(false)}/>
            {!hasPermissions &&
                <UnblockCameraMicModal open={showPermissionsError} onClose={() => setShowPermissionsError(false)}/>}
        </Layout>
    );
};

export default TalentInterview;

const UnblockCameraMicModal = ({open, onClose}) => {

    return (
        <Modal
            okText={null}
            zIndex={199}
            footer={null}
            cancelText={null}
            title={null}
            open={open}
            closeable={false}
            maskClosable={true}
            style={{top: 140}}
            destroyOnClose={true}
            wrapClassName="p-0"
            className="p-3"
            onCancel={onClose}
        >

            <div className="max-w-md mx-auto overflow-hidden">
                <div>
                    <Title level={4} className="mb-4">Unblock your camera and mic</Title>
                    <Alert
                        message="Your browser needs camera and microphone access."
                        description={
                            <ol className="list-decimal list-inside mt-2">
                                <li className="mb-2">
                                    Click the camera icon <VideoCameraFilled className="text-black"/> and/or the
                                    microphone icon <AudioFilled className="text-black"/> in your browser's address bar.
                                </li>
                                <li>Select "Allow", then click Done</li>
                            </ol>
                        }
                        type="info"
                        showIcon
                        icon={<InfoCircleOutlined/>}
                        className="bg-gray-50 mb-4"
                    />
                    <Button type="default" className="ml-auto bg-black hover:bg-black text-white border-none"
                            onClick={onClose}>
                        Dismiss
                    </Button>
                </div>
            </div>
        </Modal>
    );
};


const ExitVideoPromptModal = ({open, onClose}) => {
    const navigate = useNavigate();
    return (
        <Modal
            okText={null}
            zIndex={199}
            footer={null}
            cancelText={null}
            title={null}
            open={open}
            closeable={false}
            maskClosable={true}
            style={{top: 140}}
            destroyOnClose={true}
            wrapClassName="p-0"
            className="p-3"
            onCancel={onClose}
        >

            <div className="max-w-md mx-auto overflow-hidden">
                <div>
                    <Title level={4} className="mb-4">Exit Video Interview</Title>

                    <p className="mb-3">
                        Are you sure you want to exit and return to your profile? Any unsaved progress will be lost.
                    </p>

                    <div className="flex flex-row items-center gap-3 justify-end">
                        <Button type="default" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="primary"
                                onClick={() => {
                                    return navigate("/talent/application/video-interview", {replace: true})
                                }}>

                            Yes, Continue
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};